import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { SuperCard } from "../../components/SuperAdminLayout";
import { Download, Trash2, Mail, Copy, Check } from "lucide-react";
import { confirmDialog } from "../../components/ConfirmDialog";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export default function SuperInterestPage() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/super-admin/interest-leads").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-[#8a8a8a] py-20 text-center" data-testid="super-interest-loading">Loading interest list…</div>;

  const leads = data.leads || [];

  const csv = () => {
    const rows = [["Email", "Registered"]];
    leads.forEach((l) => rows.push([l.email, l.created_at]));
    const blob = new Blob([rows.map((x) => x.map((c) => `"${c}"`).join(",")).join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const link = document.createElement("a");
    link.href = url; link.download = "scaretickets-interest-leads.csv"; link.click(); URL.revokeObjectURL(url);
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(leads.map((l) => l.email).join(", "));
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const remove = async (lead) => {
    const ok = await confirmDialog({
      title: "Remove from list?",
      description: `Remove ${lead.email} from the interest list? This cannot be undone.`,
      confirmLabel: "Remove", destructive: true,
    });
    if (!ok) return;
    await api.delete(`/super-admin/interest-leads/${lead.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="super-interest">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Interested Attractions</h1>
          <p className="text-[#8a8a8a] text-sm mt-1">Emails captured from the landing page “Register your interest” form.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="st-btn st-btn-ghost" onClick={copyAll} disabled={!leads.length} data-testid="interest-copy">
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy emails</>}
          </button>
          <button className="st-btn st-btn-ghost" onClick={csv} disabled={!leads.length} data-testid="interest-export"><Download size={14} /> Export CSV</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SuperCard className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-[#8a8a8a]">Total registered</div>
          <div className="text-3xl font-bold mt-1 text-[#dc2626]" data-testid="interest-count">{data.count}</div>
        </SuperCard>
      </div>

      <SuperCard className="overflow-hidden">
        {leads.length === 0 ? (
          <div className="py-16 text-center text-[#8a8a8a]" data-testid="interest-empty">
            <Mail size={28} className="mx-auto text-[#3a3a3a] mb-3" />
            No one has registered interest yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="interest-table">
            <thead className="bg-[#141414] text-[#8a8a8a] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-medium px-5 py-3">Email</th>
                <th className="text-left font-medium px-5 py-3">Registered</th>
                <th className="px-5 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-[#1a1a1a] hover:bg-[#121212]" data-testid={`interest-row-${l.id}`}>
                  <td className="px-5 py-3">
                    <a href={`mailto:${l.email}`} className="text-[#dadada] hover:text-[#dc2626] transition-colors">{l.email}</a>
                  </td>
                  <td className="px-5 py-3 text-[#9a9a9a]">{fmtDate(l.created_at)}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => remove(l)} className="text-[#7a3030] hover:text-[#dc2626]" title="Remove" data-testid={`interest-delete-${l.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SuperCard>
    </div>
  );
}
